import { useCallback, useEffect, useState } from "react";
import {
  ADS_FEED_RETENTION_MAX_DAYS,
  parseAdsAssistantOrgPrefsFromChecklistConfig,
  type AdsAssistantOrgPrefs,
} from "@/lib/adsAssistantOrgSettings";
import {
  cloneDefaultPlatforms,
  PLATFORM_KEY_RE,
  platformRowsWithStableIdsFromAds,
  type PlatformRow,
} from "@/components/location-tools/salesChecklistOrgEditorTypes";
import { requireSupabase } from "@/lib/supabase";

/** Trainer+ Ads Assistant tab — merges into full `sales_daily_checklist_config` without dropping other keys. */
export function useAdsAssistantOrgSettings(organizationId: string | null) {
  const sb = requireSupabase();
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [platformRows, setPlatformRows] = useState<PlatformRow[]>(cloneDefaultPlatforms());
  const [prefs, setPrefs] = useState<AdsAssistantOrgPrefs>(() =>
    parseAdsAssistantOrgPrefsFromChecklistConfig(null),
  );
  /** Full checklist JSON last loaded — shallow merge target for save. */
  const [configBase, setConfigBase] = useState<Record<string, unknown>>({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!organizationId) {
      setSettingsLoaded(false);
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const { data, error } = await sb
        .from("organizations")
        .select("sales_daily_checklist_config")
        .eq("id", organizationId)
        .maybeSingle();
      if (error) throw error;
      const raw = (data as { sales_daily_checklist_config?: unknown } | null)?.sales_daily_checklist_config;
      const base: Record<string, unknown> =
        raw && typeof raw === "object" && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : { version: 2 };
      setConfigBase(base);

      const ads = base["ads"] as Record<string, unknown> | null | undefined;
      setPlatformRows(platformRowsWithStableIdsFromAds(ads ?? null));
      setPrefs(parseAdsAssistantOrgPrefsFromChecklistConfig(raw ?? null));
      setSettingsLoaded(true);
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg({ text: err?.message || String(e), ok: false });
      setSettingsLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [organizationId, sb]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    if (!organizationId || !settingsLoaded) return;
    setLoading(true);
    setMsg(null);
    try {
      for (const p of platformRows) {
        if (!PLATFORM_KEY_RE.test(p.key)) throw new Error(`Invalid platform key "${p.key}".`);
        if (!p.label.trim()) throw new Error(`Platform "${p.key}" needs a label.`);
      }
      const platform_entries = platformRows.map(({ key, label, daily_slots }) => ({
        key: key.trim().toLowerCase(),
        label: label.trim(),
        daily_slots: Math.min(20, Math.max(1, daily_slots)),
      }));

      const prevAdsRaw = configBase["ads"];
      const adsPrev =
        prevAdsRaw && typeof prevAdsRaw === "object" && !Array.isArray(prevAdsRaw)
          ? { ...(prevAdsRaw as Record<string, unknown>) }
          : {};

      const adsEnabled = adsPrev.enabled !== false;

      const mergedAds: Record<string, unknown> = {
        ...adsPrev,
        ...(adsEnabled ? { enabled: true } : { enabled: false }),
        platform_entries,
        feed_retention_days: Math.min(
          ADS_FEED_RETENTION_MAX_DAYS,
          Math.max(1, Math.round(prefs.feed_retention_days)),
        ),
        show_recent_logger_names: prefs.show_recent_logger_names,
        suggestion_stats_scope_default: prefs.suggestion_stats_scope_default,
        min_exemplars_for_template_signoff: prefs.min_exemplars_for_template_signoff,
        archive_export: prefs.archive_export,
        // Gamification Phase 1 — Org Activity coaching keys.
        use_in_org_activity: prefs.use_in_org_activity,
        red_after_local_time: prefs.red_after_local_time,
        daily_max_per_platform: Math.min(20, Math.max(1, Math.round(prefs.daily_max_per_platform))),
        ghl_auto_sync_enabled: prefs.ghl_auto_sync_enabled,
        ghl_auto_sync_days_back: Math.min(90, Math.max(1, Math.round(prefs.ghl_auto_sync_days_back))),
        ghl_auto_sync_limit: Math.min(100, Math.max(1, Math.round(prefs.ghl_auto_sync_limit))),
      };

      const nextConfig: Record<string, unknown> = {
        ...configBase,
        version: typeof configBase["version"] === "number" ? (configBase["version"] as number) : 2,
        ads: mergedAds,
      };

      const { error } = await sb.rpc("admin_update_org_sales_checklist_config", {
        p_org_id: organizationId,
        p_config: nextConfig,
      });
      if (error) throw error;
      setConfigBase(nextConfig);
      setMsg({ text: "Saved org Ads settings.", ok: true });
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg({ text: err?.message || String(e), ok: false });
    } finally {
      setLoading(false);
    }
  }, [organizationId, settingsLoaded, configBase, platformRows, prefs, sb]);

  return {
    settingsLoaded,
    loading,
    msg,
    platformRows,
    setPlatformRows,
    prefs,
    setPrefs,
    save,
    reload: load,
  };
}
